import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Building2, Globe, Users, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  api,
  type AnnouncementAudience,
  type AnnouncementAudienceOptions,
  type AnnouncementDetail,
  type DepartmentRef,
  type UserRef,
} from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { AttachmentsField, useUploads } from '../components/Attachments';
import { Checkbox, Field, Select, Textarea, useToast } from '../components/ui';
import { NameField, hasDescription, nameFits } from '../components/NameField';

const OPTIONS: Array<{ kind: AnnouncementAudience; label: TranslationKey; hint: TranslationKey; icon: typeof Globe }> = [
  { kind: 'ALL', label: 'announcements.option.ALL', hint: 'announcements.option.ALLHint', icon: Globe },
  { kind: 'USERS', label: 'announcements.option.USERS', hint: 'announcements.option.USERSHint', icon: Users },
  {
    kind: 'DEPARTMENTS',
    label: 'announcements.option.DEPARTMENTS',
    hint: 'announcements.option.DEPARTMENTSHint',
    icon: Building2,
  },
];

/** The hints for someone who sends only within their own departments. */
const OWN_HINT: Record<Exclude<AnnouncementAudience, 'ALL'>, TranslationKey> = {
  USERS: 'announcements.option.USERSHintOwn',
  DEPARTMENTS: 'announcements.option.DEPARTMENTSHintOwn',
};

interface Draft {
  title: string;
  body: string;
  audience: AnnouncementAudience;
  userIds: string[];
  departmentIds: string[];
}

/**
 * Routes: `/announcements/new` and `/announcements/:id/edit`. A title, the
 * details, files and voice notes, and who sees it: everyone, people picked by
 * name, or whoever is in the departments picked. Files are added on its page
 * once it is posted, like a task's.
 *
 * Administrators may send to anyone — team leaders could from 2026-10-06 until
 * 2026-10-07. Anyone else picks from the departments they are in and the people
 * in them, and is not offered everyone. An edit keeps whoever the announcement
 * already had, so they stay listed and can be taken off.
 */
export function AnnouncementFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const { t, locale, localized } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();

  const existing = useQuery({
    queryKey: ['announcement', id],
    queryFn: () => api.get<AnnouncementDetail>(`/announcements/${id}`, { registerView: false }),
    enabled: editing,
  });
  // What this person may send to: the people and departments to pick from.
  const { data: options } = useQuery({
    queryKey: ['announcements', 'audience'],
    queryFn: () => api.get<AnnouncementAudienceOptions>('/announcements/audience'),
  });
  // The server decides; until it answers, the session's own permission says the same.
  const anyone = options?.anyone ?? can(P.ANNOUNCEMENTS_ANY_AUDIENCE);

  const [form, setForm] = useState<Draft>(() => ({
    title: '',
    body: '',
    audience: anyone ? 'ALL' : 'DEPARTMENTS',
    userIds: [],
    departmentIds: [],
  }));
  const [loaded, setLoaded] = useState(false);
  if (editing && existing.data && !loaded) {
    setLoaded(true);
    const a = existing.data;
    setForm({
      title: a.title,
      body: a.body ?? '',
      audience: a.audience,
      userIds: a.users.map((p) => p.id),
      departmentIds: a.departments.map((d) => d.id),
    });
  }

  // Everyone is for team leaders and administrators; an announcement that
  // already goes to everyone keeps the choice when its author edits it.
  const offered = OPTIONS.filter((o) => o.kind !== 'ALL' || anyone || existing.data?.audience === 'ALL');

  // Whoever posted it is never in its audience: they are not told about their own news.
  const authorId = existing.data?.createdById ?? user?.id;
  // The people to pick from, and whoever it already has — who may stay, even
  // from beyond the sender's own departments.
  const names = new Map<string, UserRef>();
  for (const p of existing.data?.users ?? []) names.set(p.id, p);
  for (const p of options?.people ?? []) names.set(p.id, p);
  const pickable = [...names.values()]
    .filter((p) => p.id !== authorId && !form.userIds.includes(p.id))
    .sort((a, b) => localized(a).localeCompare(localized(b), locale));
  const departments: Array<DepartmentRef & { people: number | null }> = [...(options?.departments ?? [])];
  for (const d of existing.data?.departments ?? []) {
    if (!departments.some((x) => x.id === d.id)) departments.push({ ...d, people: null });
  }

  // Someone in no department has nobody to send to.
  const noReach = !editing && !anyone && !!options && options.departments.length === 0;
  const missingPeople = form.audience === 'USERS' && form.userIds.length === 0;
  const missingDepartments = form.audience === 'DEPARTMENTS' && form.departmentIds.length === 0;
  const ready =
    nameFits(form.title) &&
    hasDescription(form.body) &&
    !noReach &&
    !missingPeople &&
    !missingDepartments &&
    !uploads.busy;

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: form.title,
        body: form.body,
        audience: form.audience,
        ...(form.audience === 'USERS' ? { userIds: form.userIds } : {}),
        ...(form.audience === 'DEPARTMENTS' ? { departmentIds: form.departmentIds } : {}),
      };
      return editing
        ? api.patch<AnnouncementDetail>(`/announcements/${id}`, body)
        : api.post<AnnouncementDetail>('/announcements', {
            ...body,
            fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
          });
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.setQueryData(['announcement', saved.id], saved);
      const told = formatNumber(saved.notified ?? 0, locale);
      toast.success(
        editing
          ? saved.notified
            ? t('announcements.savedTold', { n: told })
            : t('announcements.saved')
          : saved.notified
            ? t('announcements.posted', { n: told })
            : t('announcements.postedNobody'),
      );
      // In place of the form, so going back from the announcement does not reopen it.
      navigate(`/announcements/${saved.id}`, { replace: true });
    },
    onError: (e: Error & { code?: string }) =>
      toast.error(e.code === 'ANNOUNCEMENT_AUDIENCE_NOT_ALLOWED' ? t('announcements.notAllowed') : e.message),
  });

  return (
    <FormPage
      title={editing ? t('announcements.edit') : t('announcements.new')}
      subtitle={editing ? existing.data?.title : t(anyone ? 'announcements.subtitle' : 'announcements.subtitleOwn')}
      backTo={editing ? `/announcements/${id}` : '/announcements'}
      backLabel={editing && existing.data ? existing.data.title : t('nav.announcements')}
      submitLabel={editing ? t('common.save') : t('announcements.post')}
      submitting={save.isPending}
      // Saving while a file is still uploading would leave it behind.
      disabled={!ready}
      loading={editing && existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-5">
        <NameField
          label={t('announcements.titleLabel')}
          value={form.title}
          dir="auto"
          onChange={(title) => setForm({ ...form, title })}
          autoFocus
        />
        <Field label={t('announcements.details')} required>
          <Textarea
            value={form.body}
            rows={6}
            maxLength={20_000}
            dir="auto"
            onChange={(e) => setForm({ ...form, body: e.target.value })}
          />
        </Field>
        {!editing && <AttachmentsField uploads={uploads} />}

        <fieldset>
          <legend className="text-body-sm font-medium text-[var(--text-secondary)]">
            {t('announcements.whoSees')} <span className="text-primary dark:text-primary-400">*</span>
          </legend>
          {noReach && (
            <p className="mt-2 rounded-md border border-warning/35 bg-warning/8 p-3 text-body-sm text-[var(--text-primary)]">
              {t('announcements.noReach')}
            </p>
          )}
          <div
            className={clsx('mt-2 grid gap-2', offered.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2')}
            role="radiogroup"
          >
            {offered.map(({ kind, label, hint, icon: Icon }) => (
              <label
                key={kind}
                className={clsx(
                  'flex cursor-pointer gap-2.5 rounded-md border p-3 transition-colors duration-fast',
                  form.audience === kind ? 'border-primary bg-primary/8' : 'hairline hover:border-primary/40',
                )}
              >
                <input
                  type="radio"
                  name="audience"
                  value={kind}
                  checked={form.audience === kind}
                  onChange={() => setForm({ ...form, audience: kind })}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                    <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    {t(label)}
                  </span>
                  <span className="mt-0.5 block text-caption text-muted">
                    {kind === 'ALL'
                      ? t(hint, { n: options?.everyone != null ? formatNumber(options.everyone, locale) : '…' })
                      : t(anyone ? hint : OWN_HINT[kind])}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {form.audience === 'USERS' && (
            <div className={clsx('mt-3 rounded-md border p-3', missingPeople ? 'border-warning/35' : 'hairline')}>
              {form.userIds.length > 0 && (
                <ul className="mb-2 flex flex-wrap gap-1.5">
                  {form.userIds.map((userId) => {
                    const name = localized(names.get(userId));
                    return (
                      <li
                        key={userId}
                        className="inline-flex items-center gap-1 rounded-pill bg-primary/12 py-1 pe-1 ps-2.5 text-body-sm text-[var(--text-primary)] ring-1 ring-inset ring-primary/30"
                      >
                        {name}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, userIds: form.userIds.filter((u) => u !== userId) })}
                          className="rounded-full p-0.5 text-[var(--text-tertiary)] hover:bg-white/10 hover:text-[var(--text-primary)]"
                          aria-label={t('announcements.removePerson', { name })}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              <Select
                value=""
                aria-label={t('announcements.addPerson')}
                onChange={(e) => e.target.value && setForm({ ...form, userIds: [...form.userIds, e.target.value] })}
              >
                <option value="">{t('announcements.addPerson')}</option>
                {pickable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {localized(p)}
                  </option>
                ))}
              </Select>
              {missingPeople && <p className="mt-1.5 text-caption text-warning-fg dark:text-warning-dark">{t('announcements.pickPeople')}</p>}
            </div>
          )}

          {form.audience === 'DEPARTMENTS' && (
            <div className={clsx('mt-3 rounded-md border p-3', missingDepartments ? 'border-warning/35' : 'hairline')}>
              <div className="grid gap-2 sm:grid-cols-2">
                {departments.map((d) => (
                  <Checkbox
                    key={d.id}
                    label={
                      <>
                        {localized(d)}{' '}
                        {d.people != null && <span className="text-faint tabular">({formatNumber(d.people, locale)})</span>}
                      </>
                    }
                    checked={form.departmentIds.includes(d.id)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        departmentIds: e.target.checked
                          ? [...form.departmentIds, d.id]
                          : form.departmentIds.filter((x) => x !== d.id),
                      })
                    }
                  />
                ))}
              </div>
              {missingDepartments && (
                <p className="mt-2 text-caption text-warning-fg dark:text-warning-dark">{t('announcements.pickDepartments')}</p>
              )}
            </div>
          )}

          {!anyone && <p className="mt-3 text-caption text-muted">{t('announcements.everyoneAdmins')}</p>}
          <p className={clsx('text-caption text-faint', anyone ? 'mt-3' : 'mt-1')}>
            {editing ? t('announcements.editNotifyHint') : t('announcements.notifyHint')}
          </p>
        </fieldset>
      </div>
    </FormPage>
  );
}

/**
 * Route: `/announcements/:id/delete` — confirms on a page, never a popup. The
 * tasks made from it carry on.
 */
export function AnnouncementDeletePage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => api.del(`/announcements/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['announcements'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.removeQueries({ queryKey: ['announcement', id] });
      toast.success(t('announcements.deleted'));
      navigate('/announcements', { replace: true });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const announcement = useQuery({
    queryKey: ['announcement', id],
    queryFn: () => api.get<AnnouncementDetail>(`/announcements/${id}`, { registerView: false }),
    // Once it is deleted there is nothing left to load: removing it from the cache must not refetch it.
    enabled: !remove.isSuccess,
  });

  const title = announcement.data?.title ?? '';
  const tasks = announcement.data?.tasks.length ?? 0;
  return (
    <FormPage
      title={t('announcements.delete')}
      subtitle={title}
      backTo={`/announcements/${id}`}
      backLabel={title || t('nav.announcements')}
      submitLabel={t('common.delete')}
      destructive
      submitting={remove.isPending}
      loading={announcement.isLoading}
      error={announcement.error}
      onRetry={() => void announcement.refetch()}
      onSubmit={() => remove.mutate()}
    >
      <p className="text-body text-[var(--text-primary)]">{t('announcements.deleteConfirm', { title })}</p>
      {tasks > 0 && (
        <p className="mt-2 text-body-sm text-muted">
          {t('announcements.deleteTasks', { n: formatNumber(tasks, locale) })}
        </p>
      )}
    </FormPage>
  );
}
